export interface BrandApp {
  id: string;
  name: string;
  description: string;
  icon: string;
  path: string;
  enabled: boolean;
  color?: string;
  external?: boolean;
  sameOriginApp?: boolean;
}

export interface BrandConfig {
  name: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  apps: BrandApp[];
}

export type BrandKey = 'marsmen' | 'hollow' | 'easystreet';

const marsmenBaseUrl =
  import.meta.env.VITE_BRAND_APP_BASE_URL || 'https://automations.mengotomars.com';
const hollowBaseUrl =
  import.meta.env.VITE_BRAND_APP_BASE_URL || 'https://hollow.automations.easystreetbrands.com';
const automationPortalUrl =
  import.meta.env.VITE_PORTAL_URL || 'https://automations.easystreetbrands.com';
const aiCenterUrl = import.meta.env.VITE_AI_CENTER_URL || '/ai-center/';

// Configuration for each brand's available apps
export const brandConfigs: Record<BrandKey, BrandConfig> = {
  marsmen: {
    name: 'Mars Men',
    logo: '/marsmen-logo.svg',
    primaryColor: '#FF6B35', // Mars Men orange from their branding
    secondaryColor: '#FF8A5C',
    apps: [
      {
        id: 'reviews',
        name: 'Review Automation',
        description: 'AI-powered review analysis and creative highlight extraction',
        icon: '⭐',
        path: `${marsmenBaseUrl}/reviews`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'churn-dashboard',
        name: 'Churn Analytics',
        description: 'Cohort retention analysis and churn prediction dashboard',
        icon: '📈',
        path: `${marsmenBaseUrl}/churn-dashboard`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'mission-control',
        name: 'Mission Control',
        description: 'Operational dashboard for order management, rules, and insights',
        icon: '🚀',
        path: `${marsmenBaseUrl}/mission-control`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'ops-control-room',
        name: 'Operations Hub',
        description: 'Inventory, procurement, payments, costing, and launch readiness workspace',
        icon: '🧭',
        path: `${marsmenBaseUrl}/ops-control-room`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'ap-workbench',
        name: 'AP Workbench',
        description: 'Review forwarded invoices, fix vendor mappings, and run vendor onboarding (parse-only)',
        icon: '🧾',
        path: `${marsmenBaseUrl}/ap-workbench`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'influencer-manager',
        name: 'Influencer Manager',
        description: 'Manage influencer campaigns, creators, and reporting in one place',
        icon: '🧑‍🤝‍🧑',
        path: `${marsmenBaseUrl}/influencer-manager`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'nano-banana',
        name: 'Nano Banana',
        description: 'AI-powered image generation and editing for marketing materials',
        icon: '🎨',
        path: `${marsmenBaseUrl}/nano-banana`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'video-briefs',
        name: 'Video Brief Automation',
        description: 'Upload Drive footage + scripts to auto-build Notion-ready paid social briefs',
        icon: '🎬',
        path: `${marsmenBaseUrl}/video-briefs`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'video-image-generation',
        name: 'Video & Image Generation',
        description: 'Launch WaveSpeed longform plus Veo, Runway, and Flux models in one console',
        icon: '🎥',
        path: `${marsmenBaseUrl}/video-image-generation`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'creative-analytics',
        name: 'Creative Analytics',
        description: 'Interactive creative reporting for output, performance trends, and team-level creative insights',
        icon: '📊',
        path: `${marsmenBaseUrl}/creative-analytics`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'brain-dashboard',
        name: 'Brain Dashboard',
        description: 'Cross-department operating dashboard for metric health, anomalies, evidence, and Ask Brain workflows',
        icon: '🧠',
        path: `${marsmenBaseUrl}/brain-dashboard`,
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'ai-center',
        name: 'AI Center of Excellence',
        description: 'Standalone AI operating resource for Mars Men: tooling, permissions, secure build guidance, and department playbooks',
        icon: '🤖',
        path: aiCenterUrl,
        enabled: true,
        color: '#FF6B35',
        external: false,
        sameOriginApp: true,
      },
      {
        id: 'data-platform',
        name: 'Data Platform',
        description: 'Catalog of source systems, warehouse families, reporting surfaces, and source-of-truth rules',
        icon: '🛰️',
        path: '/data-platform',
        enabled: true,
        color: '#FF6B35',
        external: false
      },
      {
        id: 'superset',
        name: 'Superset',
        description: 'Interactive reporting tool for Mars Men dashboards, ad hoc analysis, and deeper operational reporting',
        icon: '🧭',
        path: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/',
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'n8n',
        name: 'Workflow Automation',
        description: 'Custom automation workflows and integrations',
        icon: '⚡',
        path: 'https://marsmen.app.n8n.cloud/',
        enabled: true,
        color: '#FF6B35',
        external: true
      },
      {
        id: 'aws-console',
        name: 'AWS Console',
        description: 'Access AWS Management Console for Mars Men account',
        icon: '☁️',
        path: `https://${import.meta.env.VITE_AWS_ACCOUNT_ID || 'marsmen-production'}.signin.aws.amazon.com/console`,
        enabled: true,
        color: '#FF9900',
        external: true
      }
    ]
  },
  hollow: {
    name: 'Hollow Socks',
    logo: '/hollow-logo.avif',
    primaryColor: '#5A7A8B', // Hollow's actual slate blue from their branding
    secondaryColor: '#7A9AAB',
    apps: [
      {
        id: 'reviews',
        name: 'Review Automation',
        description: 'AI-powered review analysis and creative highlight extraction',
        icon: '⭐',
        path: `${hollowBaseUrl}/reviews`,
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'influencer-manager',
        name: 'Influencer Manager',
        description: 'Manage influencer campaigns, creators, and reporting in one place',
        icon: '🧑‍🤝‍🧑',
        path: `${hollowBaseUrl}/influencer-manager`,
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'nano-banana',
        name: 'Nano Banana',
        description: 'AI-powered image generation and editing for marketing materials',
        icon: '🎨',
        path: `${hollowBaseUrl}/nano-banana`,
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'video-briefs',
        name: 'Video Brief Automation',
        description: 'Queue Drive footage and scripts to produce Notion creative briefs automatically',
        icon: '🎬',
        path: `${hollowBaseUrl}/video-briefs`,
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'video-image-generation',
        name: 'Video & Image Generation',
        description: 'Launch WaveSpeed longform plus Veo, Runway, and Flux models in one console',
        icon: '🎥',
        path: `${hollowBaseUrl}/video-image-generation`,
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'forecast-playground',
        name: 'Forecast Playground',
        description: 'Inventory forecasting + stockout projections (Shopify + ShipHero + GoodDay).',
        icon: '📦',
        path: `${hollowBaseUrl}/forecast-playground`,
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'n8n',
        name: 'Workflow Automation', 
        description: 'Custom automation workflows and integrations',
        icon: '⚡',
        path: 'https://easystreetbrands.app.n8n.cloud/',
        enabled: true,
        color: '#5A7A8B',
        external: true
      },
      {
        id: 'aws-console',
        name: 'AWS Console',
        description: 'Access AWS Management Console for Hollow Socks account',
        icon: '☁️',
        path: `https://${import.meta.env.VITE_AWS_ACCOUNT_ID || 'hollow-production'}.signin.aws.amazon.com/console`,
        enabled: true,
        color: '#FF9900',
        external: true
      }
    ]
  },
  easystreet: {
    name: 'Easy Street Brands',
    logo: '/es-logo.svg',
    primaryColor: '#6366F1',
    secondaryColor: '#A855F7',
    apps: [
      {
        id: 'holy-shipped',
        name: 'Holy Shipped Reports',
        description: 'Cross-brand weekly delivery reports covering Mars Men and Hollow Socks.',
        icon: '🚢',
        path: `${automationPortalUrl}/holy-shipped`,
        enabled: true,
        external: true,
      },
      {
        id: 'automation-portal',
        name: 'Automation Portal',
        description: 'Parent-level control center for all brand automations.',
        icon: '🛠️',
        path: automationPortalUrl,
        enabled: true,
        external: true,
      },
    ],
  }
};

// Get brand from environment variable or domain
export function getCurrentBrand(): BrandKey {
  // Check environment variable first
  const envBrand = import.meta.env.VITE_BRAND;
  if (envBrand && (envBrand === 'marsmen' || envBrand === 'hollow' || envBrand === 'easystreet')) {
    return envBrand;
  }
  
  // Detect from domain
  const hostname = window.location.hostname;
  if (hostname.includes('marsmen') || hostname.includes('mengotomars.com')) {
    return 'marsmen';
  } else if (hostname.includes('hollow') || hostname.includes('hollowsocks.com')) {
    return 'hollow';
  } else if (hostname.includes('automations') || hostname.includes('easystreetbrands')) {
    return 'easystreet';
  }
  
  // Default to marsmen for local development
  return 'marsmen';
}

export function getBrandConfig(): BrandConfig {
  const brand = getCurrentBrand();
  return brandConfigs[brand];
}
